/**
 * FERP Portal - Repository Factory
 *
 * Creates type-safe repositories with automatic:
 * - Tenant ID injection from JWT claim
 * - Audit logging for all operations
 * - Optimistic updates for mutations
 * - Cache invalidation strategies
 *
 * CONSTRAINTS:
 * - All data access through repositories (NO direct useQuery)
 * - Tenant ID from JWT only (SINGLE SOURCE OF TRUTH)
 * - All operations logged for audit trail
 */

import { useCallback, useMemo } from 'react';
// eslint-disable-next-line no-restricted-imports -- Repository factory requires direct Apollo access
import { useQuery, useMutation, useApolloClient } from '@apollo/client/react';
import { type DocumentNode, type TypedDocumentNode, type QueryOptions, type MutationOptions, type OperationVariables, type ApolloCache, type FetchResult,  } from '@apollo/client';

import { useTenantId } from '@/auth';
import { useAuditLogger, type AuditOperation } from './useAuditLogger';
import type { BackendId } from '@/types/branded-ids';

/**
 * Base entity interface - all entities must have an id
 */
export interface BaseEntity {
  id: BackendId;
  __typename?: string;
}

/**
 * Repository configuration
 */
export interface RepositoryConfig<
  TEntity extends BaseEntity,
  TListData = { items: TEntity[] },
  TGetData = TEntity,
  TCreateInput = Partial<TEntity>,
  TUpdateInput = Partial<TEntity>,
> {
  /** Entity name for logging and cache management */
  entityName: string;

  /** GraphQL query for listing entities */
  listQuery: DocumentNode | TypedDocumentNode<TListData, OperationVariables>;

  /** GraphQL query for getting a single entity */
  getQuery: DocumentNode | TypedDocumentNode<TGetData, OperationVariables>;

  /** GraphQL mutation for creating an entity */
  createMutation?: DocumentNode | TypedDocumentNode<TEntity, OperationVariables>;

  /** GraphQL mutation for updating an entity */
  updateMutation?: DocumentNode | TypedDocumentNode<TEntity, OperationVariables>;

  /** GraphQL mutation for deleting an entity */
  deleteMutation?: DocumentNode | TypedDocumentNode<{ success: boolean }, OperationVariables>;

  /** Extract items from list query result */
  extractList?: (data: TListData) => TEntity[];

  /** Extract entity from get query result */
  extractEntity?: (data: TGetData) => TEntity;

  /** ID field name in variables (defaults to `${entityName.toLowerCase()}Id`) */
  idFieldName?: string;

  /** Enable optimistic updates for mutations */
  optimisticUpdates?: boolean;

  /** Cache invalidation strategy */
  cacheInvalidation?: 'refetch' | 'update' | 'evict';
}

/**
 * Repository hook return type
 */
export interface Repository<
  TEntity extends BaseEntity,
  TListData = { items: TEntity[] },
  TGetData = TEntity,
  TCreateInput = Partial<TEntity>,
  TUpdateInput = Partial<TEntity>,
> {
  /** List all entities for the current tenant */
  useList: (
    variables?: Record<string, unknown>,
    // @ts-expect-error Type constraint
    options?: Omit<QueryOptions<TListData, OperationVariables>, 'variables'>
  ) => {
    data: TEntity[];
    loading: boolean;
    error: Error | undefined;
    refetch: () => void;
  };

  /** Get a single entity by ID */
  useGet: (
    id: BackendId | null | undefined,
    // @ts-expect-error Type constraint
    options?: Omit<QueryOptions<TGetData, OperationVariables>, 'variables' | 'skip'>
  ) => {
    data: TEntity | null;
    loading: boolean;
    error: Error | undefined;
    refetch: () => void;
  };

  /** Create a new entity */
  useCreate: () => {
    create: (input: TCreateInput) => Promise<TEntity>;
    loading: boolean;
    error: Error | undefined;
  };

  /** Update an existing entity */
  useUpdate: () => {
    update: (id: BackendId, input: TUpdateInput) => Promise<TEntity>;
    loading: boolean;
    error: Error | undefined;
  };

  /** Delete an entity */
  useDelete: () => {
    delete: (id: BackendId) => Promise<boolean>;
    loading: boolean;
    error: Error | undefined;
  };

  /** Entity name (for display purposes) */
  entityName: string;
}

/**
 * Create a repository hook for an entity type
 */
export function createRepository<
  TEntity extends BaseEntity,
  TListData = { items: TEntity[] },
  TGetData = TEntity,
  TCreateInput = Partial<TEntity>,
  TUpdateInput = Partial<TEntity>,
>(
  config: RepositoryConfig<TEntity, TListData, TGetData, TCreateInput, TUpdateInput>
): () => Repository<TEntity, TListData, TGetData, TCreateInput, TUpdateInput> {
  const {
    entityName,
    listQuery,
    getQuery,
    createMutation,
    updateMutation,
    deleteMutation,
    extractList = (data: any) => data?.items || data?.[`${entityName.toLowerCase()}s`] || [],
    extractEntity = (data: any) => data?.[entityName.toLowerCase()] || data,
    idFieldName = `${entityName.toLowerCase()}Id`,
    optimisticUpdates = true,
    cacheInvalidation = 'update',
  } = config;

  return function useRepository(): Repository<
    TEntity,
    TListData,
    TGetData,
    TCreateInput,
    TUpdateInput
  > {
    const tenantId = useTenantId();
    const client = useApolloClient();
    const logger = useAuditLogger(entityName);

    /**
     * List hook with tenant injection
     */
    const useList = useCallback(
      (
        variables?: Record<string, unknown>,
        // @ts-expect-error Type constraint
        options?: Omit<QueryOptions<TListData, OperationVariables>, 'variables'>
      ) => {
        const trace = logger.trace('list', variables);

        const result = useQuery<TListData>(listQuery, {
          ...options,
          variables: { tenantId, ...variables },
          skip: !tenantId,
          // @ts-expect-error No overload match
          onCompleted: (data: any) => {
            trace.success();
            // @ts-expect-error Property access
            options?.onCompleted?.(data);
          },
          onError: (error: any) => {
            trace.failure(error);
            // @ts-expect-error Property access
            options?.onError?.(error);
          },
        });

        return {
          // @ts-expect-error Argument type
          data: result.data ? extractList(result.data) : [],
          loading: result.loading,
          error: result.error,
          refetch: result.refetch,
        };
      },
      [tenantId, logger]
    );

    /**
     * Get hook with tenant injection
     */
    const useGet = useCallback(
      (
        id: BackendId | null | undefined,
        // @ts-expect-error Type constraint
        options?: Omit<QueryOptions<TGetData, OperationVariables>, 'variables' | 'skip'>
      ) => {
        const trace = logger.trace('get', { id });

        const result = useQuery<TGetData>(getQuery, {
          ...options,
          variables: { tenantId, [idFieldName]: id },
          skip: !tenantId || !id,
          // @ts-expect-error No overload match
          onCompleted: (data: any) => {
            trace.success(id || undefined);
            // @ts-expect-error Property access
            options?.onCompleted?.(data);
          },
          onError: (error: any) => {
            trace.failure(error, id || undefined);
            // @ts-expect-error Property access
            options?.onError?.(error);
          },
        });

        return {
          // @ts-expect-error Argument type
          data: result.data ? extractEntity(result.data) : null,
          loading: result.loading,
          error: result.error,
          refetch: result.refetch,
        };
      },
      [tenantId, logger, idFieldName]
    );

    /**
     * Create mutation with tenant injection
     */
    const useCreate = useCallback(() => {
      if (!createMutation) {
        return {
          create: async () => {
            throw new Error(`Create mutation not configured for ${entityName}`);
          },
          loading: false,
          error: undefined,
        };
      }

      const [mutate, { loading, error }] = useMutation<TEntity>(createMutation);

      const create = async (input: TCreateInput): Promise<TEntity> => {
        const trace = logger.trace('create', { input: input as Record<string, unknown> });

        try {
          const result = await mutate({
            variables: { tenantId, input },
            update: (cache, { data }) => {
              if (cacheInvalidation === 'evict') {
                // Evict all queries for this entity type
                cache.evict({ fieldName: `${entityName.toLowerCase()}s` });
                cache.gc();
              } else if (cacheInvalidation === 'update' && data) {
                // Update strategy: Add new entity to existing list cache
                try {
                  const existingData = cache.readQuery<TListData>({
                    query: listQuery,
                    variables: { tenantId },
                  });

                  if (existingData) {
                    // Cast to TListData for compatibility with extractList
                    const existingItems = extractList(existingData as unknown as TListData);
                    const newEntity = data as TEntity;

                    // Prepend new entity to the list
                    const updatedItems = [newEntity, ...existingItems];

                    // Write back to cache with updated list - use any cast for Apollo type compatibility
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    cache.writeQuery<any>({
                      query: listQuery,
                      variables: { tenantId },
                      data: {
                        ...existingData,
                        items: updatedItems,
                        [`${entityName.toLowerCase()}s`]: updatedItems,
                      },
                    });
                  }
                } catch {
                  // Cache miss - no existing data to update, that's OK
                }
              }
              // 'refetch' strategy is handled by Apollo's refetchQueries option
            },
          });

          const entity = result.data as TEntity;
          trace.success(entity?.id);
          return entity;
        } catch (err) {
          trace.failure(err as Error);
          throw err;
        }
      };

      return { create, loading, error };
    }, [tenantId, logger, cacheInvalidation]);

    /**
     * Update mutation with tenant injection
     */
    const useUpdate = useCallback(() => {
      if (!updateMutation) {
        return {
          update: async () => {
            throw new Error(`Update mutation not configured for ${entityName}`);
          },
          loading: false,
          error: undefined,
        };
      }

      const [mutate, { loading, error }] = useMutation<TEntity>(updateMutation);

      const update = async (id: BackendId, input: TUpdateInput): Promise<TEntity> => {
        const trace = logger.trace('update', { id, input: input as Record<string, unknown> });

        try {
          const result = await mutate({
            variables: { tenantId, [idFieldName]: id, input },
            optimisticResponse: optimisticUpdates
              ? ({
                  __typename: 'Mutation',
                  [`update${entityName}`]: {
                    __typename: entityName,
                    id,
                    ...input,
                  },
                } as unknown as TEntity)
              : undefined,
          });

          const entity = result.data as TEntity;
          trace.success(id);
          return entity;
        } catch (err) {
          trace.failure(err as Error, id);
          throw err;
        }
      };

      return { update, loading, error };
    }, [tenantId, logger, idFieldName, optimisticUpdates]);

    /**
     * Delete mutation with tenant injection
     */
    const useDelete = useCallback(() => {
      if (!deleteMutation) {
        return {
          delete: async () => {
            throw new Error(`Delete mutation not configured for ${entityName}`);
          },
          loading: false,
          error: undefined,
        };
      }

      const [mutate, { loading, error }] = useMutation<{ success: boolean }>(deleteMutation);

      const deleteEntity = async (id: BackendId): Promise<boolean> => {
        const trace = logger.trace('delete', { id });

        try {
          const result = await mutate({
            variables: { tenantId, [idFieldName]: id },
            update: (cache) => {
              // Remove from cache
              cache.evict({ id: cache.identify({ __typename: entityName, id }) });
              cache.gc();
            },
          });

          trace.success(id);
          return result.data?.success ?? true;
        } catch (err) {
          trace.failure(err as Error, id);
          throw err;
        }
      };

      return { delete: deleteEntity, loading, error };
    }, [tenantId, logger, idFieldName]);

    return {
      useList,
      useGet,
      useCreate,
      useUpdate,
      useDelete,
      entityName,
    };
  };
}

export default createRepository;
